import type { EntityRegistry } from './entityRegistry';
import type { EventBus } from './eventBus';
import type { Worm } from '../entities/worm';
import type { Mine } from '../weapons/mine';
import type { Grave } from '../entities/grave';
import type { Crate } from '../entities/crate';
import type { OilBarrel } from '../entities/oilBarrel';
import { calculateExplosionDamage } from '../terrain/destruction';
import { WA_PHYSICS } from './constants';

export interface ExplosionManagerCallbacks {
  destroyTerrain: (x: number, y: number, radius: number) => void;
  emitParticles: (x: number, y: number, radius: number) => void;
  playExplosionSound: () => void;
  playDamageSound: () => void;
  flashWormHurt: (worm: Worm) => void;
  updateTerrainTexture: () => void;
  shakeCamera: (intensity: number, duration: number) => void;
  setThrownWorm: (worm: Worm) => void;
  checkGameOver: () => boolean;
  broadcastExplosion: (data: { x: number; y: number; radius: number; maxDamage: number }) => void;
  broadcastGameOver: () => void;
  getTeams: () => Array<{ worms: Worm[] }>;
  isAuthority: () => boolean;
  shouldShakeCamera: () => boolean;
  /** Mode-level global scale on explosion knockback (GunBound tones it down).
   *  Defaults to 1 when not provided. */
  getExplosionPushScale?: () => number;
}

export interface ExplosionManagerDependencies {
  entityRegistry: EntityRegistry;
  eventBus: EventBus;
}

export class ExplosionManager {
  private deps: ExplosionManagerDependencies;
  private callbacks: ExplosionManagerCallbacks;

  constructor(deps: ExplosionManagerDependencies, callbacks: ExplosionManagerCallbacks) {
    this.deps = deps;
    this.callbacks = callbacks;
  }

  createExplosion(
    x: number,
    y: number,
    radius: number,
    maxDamage: number,
    fromNetwork = false,
    directHitWorm?: Worm,
    pushMultiplier = 1,
    skipTerrain = false,
    magnet?: { radius: number; strength: number },
  ): void {
    if (!skipTerrain) {
      this.callbacks.destroyTerrain(x, y, radius);
    }
    this.callbacks.emitParticles(x, y, radius);
    this.callbacks.playExplosionSound();
    this.callbacks.updateTerrainTexture();

    this.deps.eventBus.emit('entity:exploded', {
      type: 'projectile',
      id: 'explosion',
      position: { x, y },
      radius,
      maxDamage,
    });

    const amAuthority = this.callbacks.isAuthority();

    if (amAuthority) {
      this.applyWormDamage(x, y, radius, maxDamage, directHitWorm, pushMultiplier);
      // J.D. magnet: a wide pull/push over its own radius, separate from the
      // (minimal) blast. Authority only - it rides the worm push state, which
      // online clients reconcile from snapshots like any explosion knockback.
      if (magnet) this.applyMagnet(x, y, magnet.radius, magnet.strength);
      this.applyMineForces(x, y, radius);
      this.applyGraveDamage(x, y, radius, maxDamage);
      this.applyCrateEffects(x, y, radius);
      this.applyBarrelEffects(x, y, radius);

      const gameEnded = this.callbacks.checkGameOver();

      if (!fromNetwork) {
        this.callbacks.broadcastExplosion({ x, y, radius, maxDamage });
        if (gameEnded) {
          this.callbacks.broadcastGameOver();
        }
      }
    }

    if (this.callbacks.shouldShakeCamera()) {
      // Punchier than before (was radius/5, 200ms): bigger blasts hit harder.
      this.callbacks.shakeCamera(Math.min(28, radius / 3 + 4), 320);
    }
  }

  private applyWormDamage(
    x: number,
    y: number,
    radius: number,
    maxDamage: number,
    directHitWorm?: Worm,
    pushMultiplier = 1,
  ): void {
    let mostThrownWorm: Worm | null = null;
    let highestVelocity = 0;

    for (const team of this.callbacks.getTeams()) {
      for (const worm of team.worms) {
        if (worm.state === 'dead') continue;

        const center = worm.getCenterPosition();
        const pushScale = this.callbacks.getExplosionPushScale?.() ?? 1;
        let { damage, pushX, pushY } = calculateExplosionDamage(
          x,
          y,
          radius,
          maxDamage,
          center.x,
          center.y,
          pushMultiplier * pushScale,
        );

        if (directHitWorm && worm.id === directHitWorm.id) {
          const directHitBonus = Math.round(maxDamage * 0.3);
          damage = Math.max(damage, directHitBonus);
          pushX *= 1.5;
          pushY *= 1.5;
        }

        if (damage > 0) {
          worm.addPendingDamage(damage);
          worm.applyExplosionPush(pushX, pushY);

          this.callbacks.playDamageSound();
          this.callbacks.flashWormHurt(worm);

          const velocity = Math.sqrt(pushX * pushX + pushY * pushY);
          if (velocity > highestVelocity) {
            highestVelocity = velocity;
            mostThrownWorm = worm;
          }
        }
      }
    }

    if (mostThrownWorm) {
      this.callbacks.setThrownWorm(mostThrownWorm);
    }
  }

  /**
   * J.D.'s magnetic field: pull (strength<0) every live worm toward the impact
   * or shove (strength>0) it away, over `radius` (much larger than the blast),
   * weakening LINEARLY with distance (full force at the centre, 0 at the edge).
   * Pure radial, independent of the mode's explosionPushScale.
   */
  private applyMagnet(x: number, y: number, radius: number, strength: number): void {
    if (radius <= 0 || strength === 0) return;
    for (const team of this.callbacks.getTeams()) {
      for (const worm of team.worms) {
        if (worm.state === 'dead') continue;
        const c = worm.getCenterPosition();
        const dx = c.x - x;
        const dy = c.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist >= radius || dist < 0.001) continue;
        const falloff = 1 - dist / radius; // weaker the farther from the impact
        const mag = falloff * strength; // <0 pulls toward the impact, >0 shoves away
        worm.applyExplosionPush((dx / dist) * mag, (dy / dist) * mag);
      }
    }
  }

  private applyMineForces(x: number, y: number, radius: number): void {
    for (const mine of this.deps.entityRegistry.getAll<Mine>('mine')) {
      const dx = mine.position.x - x;
      const dy = mine.position.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < radius * 1.5 && dist > 0) {
        const force = (1 - dist / (radius * 1.5)) * WA_PHYSICS.EXPLOSION.PUSH_FORCE * 100;
        mine.applyExplosionForce((dx / dist) * force, (dy / dist) * force);
      }
    }
  }

  private applyGraveDamage(x: number, y: number, radius: number, maxDamage: number): void {
    for (const grave of this.deps.entityRegistry.getAll<Grave>('grave')) {
      if (!grave.active) continue;
      const dx = grave.position.x - x;
      const dy = grave.position.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < radius * 1.2 && dist > 0) {
        const { damage, pushX, pushY } = calculateExplosionDamage(
          x,
          y,
          radius,
          maxDamage,
          grave.position.x,
          grave.position.y,
        );
        if (damage > 0) {
          grave.takeDamage(damage, pushX, pushY);
        }
      }
    }
    this.deps.entityRegistry.removeInactive('grave');
  }

  private applyCrateEffects(x: number, y: number, radius: number): void {
    for (const crate of this.deps.entityRegistry.getAll<Crate>('crate')) {
      if (!crate.active) continue;
      const dx = crate.position.x - x;
      const dy = crate.position.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < radius * 1.2 && dist > 0) {
        crate.explode();
      } else if (dist < radius * 2 && dist > 0) {
        const force = (1 - dist / (radius * 2)) * WA_PHYSICS.EXPLOSION.PUSH_FORCE * 80;
        crate.applyExplosionForce((dx / dist) * force, (dy / dist) * force);
      }
    }
    this.deps.entityRegistry.removeInactive('crate');
  }

  private applyBarrelEffects(x: number, y: number, radius: number): void {
    for (const barrel of this.deps.entityRegistry.getAll<OilBarrel>('barrel')) {
      if (!barrel.active) continue;
      const dx = barrel.position.x - x;
      const dy = barrel.position.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < radius * 1.3) {
        barrel.takeDamage(1);
      } else if (dist < radius * 2 && dist > 0) {
        const force = (1 - dist / (radius * 2)) * WA_PHYSICS.EXPLOSION.PUSH_FORCE * 80;
        barrel.applyExplosionForce((dx / dist) * force, (dy / dist) * force);
      }
    }
    this.deps.entityRegistry.removeInactive('barrel');
  }
}
